import clsx from "clsx";

export type TypographyVariant =
    | "display-large"
    | "display-medium"
    | "display-small"
    | "headline-large"
    | "headline-medium"
    | "headline-small"
    | "title-large"
    | "title-medium"
    | "title-small"
    | "body-large"
    | "body-medium"
    | "body-small"
    | "label-large"
    | "label-medium"
    | "label-small";

export type TypographyComponent = "h1" | "h2" | "h3" | "p" | "span" | "label";

export type TypographyProps = {
    children: React.ReactNode;
    variant: TypographyVariant;
    component: TypographyComponent;
    gutterBottom?: boolean;
    className?: string;
    underline?: boolean;
};

const variantClasses: { [key in TypographyVariant]: string } = {
    "display-large": "text-[3.563rem] leading-[4rem] font-semibold",
    "display-medium": "text-[2.813rem] leading-[3.25rem] font-semibold",
    "display-small": "text-[2.25rem] leading-[2.75rem] font-semibold",
    "headline-large": "text-[2rem] font-semibold leading-[2.5rem]",
    "headline-medium": "text-[1.75rem] leading-[2.25rem] font-semibold",
    "headline-small": "text-[1.5rem] leading-[2rem] font-semibold",
    "title-large": "text-[1.375rem] leading-[1.75rem]",
    "title-medium": "leading-[1.5rem] font-medium",
    "title-small": "text-[0.875rem] leading-[1.25rem] font-medium",
    "body-large": "leading-[1.5rem]",
    "body-medium": "text-[0.875rem] leading-[1.25rem]",
    "body-small": "text-[0.75rem] leading-[1rem]",
    "label-large": "text-sm leading-5 font-semibold",
    "label-medium": "text-xs leading-4 font-semibold",
    "label-small": "text-[0.688rem] leading-4 font-semibold",
};

const gutters: { [key in TypographyVariant]: string } = {
    "display-large": "mb-8",
    "display-medium": "mb-8",
    "display-small": "mb-8",
    "headline-large": "mb-4",
    "headline-medium": "mb-4",
    "headline-small": "mb-4",
    "title-large": "mb-2",
    "title-medium": "mb-2",
    "title-small": "mb-2",
    "body-large": "mb-2",
    "body-medium": "mb-2",
    "body-small": "mb-2",
    "label-large": "mb-2",
    "label-medium": "mb-2",
    "label-small": "mb-2",
};

const Typography = ({
    children,
    component,
    variant,
    gutterBottom,
    className,
    underline,
}: TypographyProps) => {
    const Component = component;
    return (
        <Component
            className={clsx(
                variantClasses[variant],
                gutterBottom && gutters[variant],
                underline && "underline decoration-primary underline-offset-8",
                className
            )}
        >
            {children}
        </Component>
    );
};

export default Typography;
